import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import type { ClaimProfile, FilingPackageItem, PackageDocumentField } from '../types';
import { formatUsDate } from './profile';

interface FillContext {
  claimName: string;
  profile: ClaimProfile;
  signedDate: string;
}

function resolveSource(source: string | null, ctx: FillContext): string {
  if (!source) return '';
  if (source === 'claim.name' || source === 'claim.company') return ctx.claimName;
  if (source === 'claimant.name' || source === 'full_name' || source === 'profile.full_name') {
    return [ctx.profile.first_name, ctx.profile.middle_name, ctx.profile.last_name]
      .filter((part) => part.trim())
      .join(' ');
  }
  if (source.startsWith('profile.')) {
    const key = source.slice('profile.'.length) as keyof ClaimProfile;
    return ctx.profile[key] ?? '';
  }
  if (
    source === 'date' ||
    source === 'today' ||
    source === 'signed_at' ||
    source === 'filing.signed_at' ||
    source.endsWith('.date') ||
    source.includes('signed_date')
  ) {
    return ctx.signedDate;
  }
  return '';
}

function fitFontSize(height: number): number {
  return Math.max(7, Math.min(12, height * 0.62));
}

type PdfFont = Awaited<ReturnType<PDFDocument['embedFont']>>;

function canEncodeWithFont(font: PdfFont, text: string): boolean {
  try {
    font.widthOfTextAtSize(text, 10);
    return true;
  } catch {
    return false;
  }
}

async function renderFieldTextPng(
  text: string,
  widthPt: number,
  heightPt: number,
  fontSizePt: number,
): Promise<Uint8Array> {
  const scale = 3;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(widthPt * scale));
  canvas.height = Math.max(1, Math.ceil(heightPt * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.scale(scale, scale);
  ctx.clearRect(0, 0, widthPt, heightPt);
  ctx.fillStyle = '#1a1613';
  ctx.font = `${fontSizePt}px "Hanken Grotesk Variable", "Hanken Grotesk", sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(text, 2, heightPt / 2, Math.max(4, widthPt - 4));
  return canvasToPngBytes(canvas);
}

export async function fillAuthorizationPdf(params: {
  templateBytes: ArrayBuffer;
  item: FilingPackageItem;
  profile: ClaimProfile;
  signaturePng?: Uint8Array;
}): Promise<Uint8Array> {
  const pdf = await PDFDocument.load(params.templateBytes);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const pages = pdf.getPages();
  const fields = params.item.document?.fields ?? [];
  const ctx: FillContext = {
    claimName: params.item.name,
    profile: params.profile,
    signedDate: formatUsDate(),
  };

  let signatureImage: Awaited<ReturnType<PDFDocument['embedPng']>> | null = null;
  if (params.signaturePng) {
    signatureImage = await pdf.embedPng(params.signaturePng);
  }

  for (const field of fields) {
    await drawField(pdf, pages, font, field, ctx, signatureImage);
  }

  return pdf.save();
}

async function drawField(
  pdf: PDFDocument,
  pages: ReturnType<PDFDocument['getPages']>,
  font: PdfFont,
  field: PackageDocumentField,
  ctx: FillContext,
  signatureImage: Awaited<ReturnType<PDFDocument['embedPng']>> | null,
) {
  const page = pages[field.placement.page_index];
  if (!page) return;
  const { x, y, width, height } = field.placement;

  if (field.type === 'signature') {
    if (!signatureImage) return;
    const scale = Math.min(width / signatureImage.width, height / signatureImage.height);
    const w = signatureImage.width * scale;
    const h = signatureImage.height * scale;
    page.drawImage(signatureImage, {
      x,
      y: y + (height - h) / 2,
      width: w,
      height: h,
    });
    return;
  }

  const text = resolveSource(field.source, ctx);
  if (!text) return;
  const size = fitFontSize(height);

  if (canEncodeWithFont(font, text)) {
    page.drawText(text, {
      x: x + 2,
      y: y + (height - size) / 2,
      size,
      font,
      color: rgb(0.1, 0.09, 0.07),
      maxWidth: Math.max(8, width - 4),
    });
    return;
  }

  const png = await renderFieldTextPng(text, width, height, size);
  const image = await pdf.embedPng(png);
  page.drawImage(image, { x, y, width, height });
}

export function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not export signature'));
        return;
      }
      blob.arrayBuffer().then((buf) => resolve(new Uint8Array(buf)), reject);
    }, 'image/png');
  });
}

export function renderTypedSignaturePng(name: string, width = 520, height = 92): Promise<Uint8Array> {
  const canvas = document.createElement('canvas');
  const dpr = 2;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Canvas unavailable'));
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#1a1613';
  ctx.font = 'italic 32px Georgia, "Times New Roman", cursive';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(name.trim(), 8, height / 2, width - 16);
  return canvasToPngBytes(canvas);
}
